import { useState, useEffect, useRef } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? '' }
  catch { return '' }
}

interface Deployment { id: string; status: string; url: string | null; provider: string; created_at: string }
interface BuildConfig { build_cmd?: string; output_dir?: string; install_cmd?: string; node_version?: string; env_vars?: Record<string, string> }

export function DeployPanel({ projectSlug }: { projectSlug?: string } = {}) {
  const [tab, setTab] = useState<'deploy' | 'history' | 'config'>('deploy')
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [logs, setLogs] = useState<string[]>([])
  const [deploying, setDeploying] = useState(false)
  const [result, setResult] = useState<{ status: string; url?: string } | null>(null)
  const [config, setConfig] = useState<BuildConfig>({})
  const [provider, setProvider] = useState('cloudflare')
  const [projectDir, setProjectDir] = useState(projectSlug || '')
  const [projects, setProjects] = useState<string[]>([])
  const logsEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    authFetch(apiUrl('/api/deploy')).then((r) => r.json()).then(setDeployments).catch(() => {})
    authFetch(apiUrl('/api/deploy/config/current')).then((r) => r.json()).then((c) => { if (c) setConfig(c) }).catch(() => {})
    // Load deployable projects (folders with package.json or index.html)
    authFetch(apiUrl('/api/deploy/projects')).then(r => r.json()).then(list => {
      if (Array.isArray(list)) {
        setProjects(list)
        if (list.length > 0 && !projectDir) setProjectDir(list[0])
      }
    }).catch(() => {
      // Fallback: load top-level folders
      authFetch(apiUrl('/api/files/list')).then(r => r.json()).then(tree => {
        const folders = (Array.isArray(tree) ? tree : []).filter((f: any) => f.type === 'folder').map((f: any) => f.name)
        setProjects(folders)
        if (folders.length > 0 && !projectDir) setProjectDir(folders[0])
      }).catch(() => {})
    })
  }, [])

  useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [logs])

  const deploy = async () => {
    setDeploying(true)
    setLogs([])
    setResult(null)
    setTab('deploy')

    try {
      const res = await authFetch(apiUrl('/api/deploy'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_dir: projectDir || undefined,
          build_cmd: config.build_cmd || undefined,
          output_dir: config.output_dir || undefined,
          env_vars: config.env_vars || undefined,
          provider,
        }),
      })

      if (!res.body) { setDeploying(false); return }
      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'log') setLogs((l) => [...l, data.text])
            if (data.type === 'done') {
              setResult({ status: data.status, url: data.url })
              authFetch(apiUrl('/api/deploy')).then((r) => r.json()).then(setDeployments).catch(() => {})
            }
          } catch {}
        }
      }
    } catch (e: any) {
      setResult({ status: 'failed' })
    } finally {
      setDeploying(false)
    }
  }

  const rollback = async (id: string) => {
    if (!window.confirm('Rollback to this deployment?')) return
    await authFetch(apiUrl(`/api/deploy/${id}/rollback`), { method: 'POST' })
    authFetch(apiUrl('/api/deploy')).then((r) => r.json()).then(setDeployments).catch(() => {})
  }

  const saveConfig = async () => {
    await authFetch(apiUrl('/api/deploy/config'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    })
  }

  const checkHealth = async (url: string) => {
    const res = await authFetch(apiUrl('/api/deploy/health'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    const data = await res.json()
    alert(data.up ? `UP - ${data.status} (${data.responseTime}ms)` : `DOWN (${data.responseTime}ms)`)
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span>DEPLOY</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['deploy', 'history', 'config'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
              border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
              background: tab === t ? 'rgba(124,110,245,0.1)' : 'none',
              color: tab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {tab === 'deploy' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
              <select value={projectDir} onChange={(e) => setProjectDir(e.target.value)} style={{ ...inputStyle, flex: 2 }}>
                <option value="">Workspace root</option>
                {projects.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={provider} onChange={(e) => setProvider(e.target.value)} style={{ ...inputStyle, width: 80 }}>
              <option value="cloudflare">Cloudflare Pages</option>
              <option value="pulse">PULSE (local)</option>
                <option value="vercel">Vercel (needs token in Secrets)</option>
                <option value="netlify">Netlify (needs token in Secrets)</option>
              </select>
            </div>
            <button onClick={deploy} disabled={deploying} style={{
              width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--accent-2)',
              background: deploying ? 'var(--bg-3)' : 'rgba(95,212,164,0.1)',
              color: deploying ? 'var(--text-2)' : 'var(--accent-2)', cursor: deploying ? 'wait' : 'pointer',
              fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              {deploying ? 'Building...' : 'Deploy Now'}
            </button>
          </div>

          {/* Build logs */}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px', background: 'var(--bg-0)', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-2)', lineHeight: 1.6 }}>
            {logs.length === 0 && !result && (
              <div style={{ color: 'var(--text-2)', textAlign: 'center', padding: 20 }}>
                Click "Deploy Now" to start building
              </div>
            )}
            {logs.map((l, i) => <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{l}</div>)}
            {result && (
              <div style={{ marginTop: 8, padding: '8px', borderRadius: 6, border: `1px solid ${result.status === 'deployed' ? 'var(--accent-2)' : 'var(--red)'}`, background: result.status === 'deployed' ? 'rgba(95,212,164,0.1)' : 'rgba(240,112,112,0.1)' }}>
                <div style={{ fontWeight: 700, color: result.status === 'deployed' ? 'var(--accent-2)' : 'var(--red)' }}>
                  {result.status === 'deployed' ? 'Deployed successfully!' : 'Deploy failed'}
                </div>
                {result.url && (
                  result.url.startsWith('http') ? (
                    <a href={result.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 10, display: 'block', marginTop: 4 }}>
                      🌐 {result.url}
                    </a>
                  ) : (
                    <a href={`${apiUrl(result.url)}${result.url.includes('?') ? '&' : '?'}token=${getToken()}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 10, display: 'block', marginTop: 4 }}>
                      📂 Open local preview
                    </a>
                  )
                )}
              </div>
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          {deployments.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No deployments yet</div>
          ) : deployments.map((d) => (
            <div key={d.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: d.status === 'deployed' ? 'var(--accent-2)' : d.status === 'failed' ? 'var(--red)' : 'var(--text-2)',
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>
                  {d.status} {d.provider !== 'pulse' ? `(${d.provider})` : ''}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                  {new Date(d.created_at).toLocaleString()} &middot; {d.id.slice(0, 8)}
                </div>
              </div>
              {d.url && (
                <button onClick={() => checkHealth(d.url!)} style={smallBtn} title="Health check">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                </button>
              )}
              {d.status !== 'deployed' && d.url && (
                <button onClick={() => rollback(d.id)} style={{ ...smallBtn, color: 'var(--accent-3)' }} title="Rollback">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'config' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Build Command</label>
            <input value={config.build_cmd ?? ''} onChange={(e) => setConfig({ ...config, build_cmd: e.target.value })} onBlur={saveConfig} placeholder="npm run build" style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Output Directory</label>
            <input value={config.output_dir ?? ''} onChange={(e) => setConfig({ ...config, output_dir: e.target.value })} onBlur={saveConfig} placeholder="dist" style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Install Command</label>
            <input value={config.install_cmd ?? ''} onChange={(e) => setConfig({ ...config, install_cmd: e.target.value })} onBlur={saveConfig} placeholder="npm install" style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Node Version</label>
            <select value={config.node_version ?? '20'} onChange={(e) => { setConfig({ ...config, node_version: e.target.value }); saveConfig() }} style={inputStyle}>
              <option value="18">Node 18</option>
              <option value="20">Node 20</option>
              <option value="22">Node 22</option>
            </select>
          </div>
          <div style={{ marginTop: 12, fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
            Config saved to .pulse-deploy.json
          </div>
        </div>
      )}
    </div>
  )
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const inputStyle: React.CSSProperties = { flex: 1, padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const smallBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', padding: 4 }
const fieldStyle: React.CSSProperties = { marginBottom: 10 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
